export type SentryApp = 'playground' | 'portal' | 'mobile'
export type SentryEnvironment = 'qa' | 'staging' | 'production'
export type SentryDeployMode = 'preview' | 'release'

export interface BuildSentryOptionsInput {
  dsn: string | undefined
  environment: string | undefined
  release: string | undefined
  app: SentryApp
  deployMode?: SentryDeployMode
}

export interface SentryCoreOptions {
  dsn: string
  environment: SentryEnvironment
  release: string
  initialScope: {
    tags: { app: SentryApp; deploy_mode: SentryDeployMode }
  }
  beforeSend: <E>(event: E, hint: { originalException?: unknown }) => E | null
}

const USER_FACING_FIREBASE_AUTH_CODES = new Set<string>([
  'auth/wrong-password',
  'auth/invalid-email',
  'auth/invalid-credential',
  'auth/user-not-found',
  'auth/email-already-in-use',
  'auth/weak-password',
  'auth/too-many-requests',
  'auth/invalid-verification-code',
  'auth/expired-action-code',
  'auth/missing-email',
  'auth/missing-password',
])

export function isExpectedUserFacingError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  if ((error as { name?: string }).name === 'ZodError') return true

  const code = (error as { code?: unknown }).code
  if (typeof code === 'string' && USER_FACING_FIREBASE_AUTH_CODES.has(code)) {
    return true
  }

  return false
}

export function buildSentryOptions(
  input: BuildSentryOptionsInput
): SentryCoreOptions | null {
  const { dsn, environment, release, app, deployMode = 'release' } = input
  if (!dsn || !environment || !release) return null
  return {
    dsn,
    environment: environment as SentryEnvironment,
    release,
    initialScope: { tags: { app, deploy_mode: deployMode } },
    beforeSend: (event, hint) => {
      if (isExpectedUserFacingError(hint?.originalException)) return null
      return event
    },
  }
}

const DENY_PII_KEYS = { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] }

// Sentry v11's default collects user info, cookies and request bodies; this
// keeps the v10 baseline, per Sentry's migration guide.
export const RESTRICTIVE_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: { request: DENY_PII_KEYS, response: DENY_PII_KEYS },
  httpBodies: [],
  urlQueryParams: DENY_PII_KEYS,
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
}

export function formatRelease(
  app: SentryApp,
  version: string,
  buildNumber?: string
): string {
  return buildNumber ? `${app}@${version}+${buildNumber}` : `${app}@${version}`
}
